'use client';

import { type ReactEventHandler, type ReactNode, useState } from 'react';
import { FederatedPhoto, type FederatedPhotoProps } from './index';
import { buildFederatedPhotoAttrs, type CcFtSize } from './federatedPhoto';

export type FederatedPhotoWithFallbackProps = FederatedPhotoProps & {
  fallback?: 'placeholder' | 'source' | 'none';
  fallbackContent?: ReactNode;
};

const DEFAULT_SIZE: CcFtSize = 768;

/**
 * Client-only FederatedPhoto wrapper for image surfaces where a broken Fed
 * Photos render is worse than the added hydration cost.
 *
 * Keep the default `FederatedPhoto` server-compatible for static / server
 * render paths. Use this wrapper only on already-interactive surfaces, or on
 * important listing-photo surfaces where recovering from transient Federated Photo
 * render failures is worth introducing a client boundary.
 *
 * On image-load failure, this can swap to either the 192w treatment placeholder
 * or the unmodified API source. If that fallback also fails, callers can render
 * `fallbackContent` instead of leaving a broken image.
 *
 * Caller-provided `onError` runs before the fallback so analytics / logging
 * still see the original failed URL. Pass `fallback="none"` when a caller
 * needs to observe or handle broken images itself.
 */
export function FederatedPhotoWithFallback({ src, ...props }: FederatedPhotoWithFallbackProps) {
  return <FederatedPhotoFallbackAttempt key={src} src={src} {...props} />;
}

function FederatedPhotoFallbackAttempt({
  fallback = 'placeholder',
  fallbackContent,
  src,
  defaultSize = DEFAULT_SIZE,
  treatment,
  preserveSource = false,
  onError,
  ...imageProps
}: FederatedPhotoWithFallbackProps) {
  const [attempt, setAttempt] = useState<'primary' | 'fallback' | 'exhausted'>('primary');
  const responsiveAttrs =
    preserveSource || fallback !== 'placeholder'
      ? null
      : buildFederatedPhotoAttrs(src, defaultSize, [defaultSize], { treatment });
  const fallbackSrc = fallback === 'source' && !preserveSource ? src : responsiveAttrs?.placeholder;
  const fallbackActive = attempt === 'fallback' && fallbackSrc !== undefined;
  const imageSrc = fallbackActive ? fallbackSrc : src;

  const handleError: ReactEventHandler<HTMLImageElement> = (event) => {
    onError?.(event);
    if (attempt === 'primary' && fallbackSrc) {
      setAttempt('fallback');
    } else if (attempt === 'fallback' && fallbackContent) {
      setAttempt('exhausted');
    }
  };

  if (attempt === 'exhausted') return fallbackContent;

  return (
    <FederatedPhoto
      {...imageProps}
      src={imageSrc}
      defaultSize={defaultSize}
      treatment={treatment}
      preserveSource={fallbackActive || preserveSource}
      onError={handleError}
    />
  );
}
